import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Package } from 'lucide-react';

interface Product {
  id: string;
  name: string;
  sku: string;
  description: string;
  regularPrice: number;
  stockQuantity: number;
  stockStatus: 'instock' | 'outofstock' | 'onbackorder';
  mainImage?: string;
  images?: string[];
  categories?: string[];
  companyId: string;
  createdAt: string | { seconds: number; nanoseconds: number };
  createdBy: string;
  status: 'pending';
  height?: number;
  width?: number;
  length?: number;
  weight?: number;
  woocommerce?: {
    last_synced: string;
    woocommerce_id: number;
  };
}

export default function PendingProductDetails() {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.role === 'admin') {
      navigate('/dashboard');
      return;
    }

    const loadProduct = async () => {
      if (!id) return;

      try {
        const docRef = doc(db, 'pendingProducts', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setProduct({
            id: docSnap.id,
            ...docSnap.data()
          } as Product);
        } else {
          setError('Produto não encontrado');
        }
      } catch (err) {
        console.error('Erro ao carregar produto:', err);
        setError('Erro ao carregar produto');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, currentUser, navigate]);

  const formatDate = (date: string | { seconds: number; nanoseconds: number }) => {
    if (typeof date === 'string') {
      return new Date(date).toLocaleString('pt-PT');
    }
    return new Date(date.seconds * 1000).toLocaleString('pt-PT');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error || 'Produto não encontrado'}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{product.name}</h1>
            <button
              onClick={() => navigate('/dashboard/products/pending')}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              Voltar
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              {product.mainImage ? (
                <img
                  src={product.mainImage}
                  alt={product.name}
                  className="w-full rounded-lg object-cover"
                />
              ) : (
                <div className="w-full aspect-square rounded-lg bg-gray-100 flex items-center justify-center">
                  <Package className="h-16 w-16 text-gray-400" />
                </div>
              )}

              {product.images && product.images.length > 1 && (
                <div className="mt-4 grid grid-cols-4 gap-2">
                  {product.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`${product.name} - Imagem ${index + 1}`}
                      className="w-full rounded-md object-cover aspect-square"
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Informações do Produto</h2>
                <dl className="mt-4 space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">SKU</dt>
                    <dd className="mt-1 text-sm text-gray-900">{product.sku}</dd>
                  </div>

                  <div>
                    <dt className="text-sm font-medium text-gray-500">Preço</dt>
                    <dd className="mt-1 text-sm text-gray-900">€{product.regularPrice.toFixed(2)}</dd>
                  </div>

                  <div>
                    <dt className="text-sm font-medium text-gray-500">Stock</dt>
                    <dd className="mt-1 text-sm text-gray-900">{product.stockQuantity} unidades</dd>
                  </div>

                  <div>
                    <dt className="text-sm font-medium text-gray-500">Estado do Stock</dt>
                    <dd className="mt-1 text-sm text-gray-900">{product.stockStatus}</dd>
                  </div>

                  <div>
                    <dt className="text-sm font-medium text-gray-500">Categorias</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {product.categories?.join(', ') || 'Sem categorias'}
                    </dd>
                  </div>

                  {product.description && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Descrição</dt>
                      <dd className="mt-1 text-sm text-gray-900">{product.description}</dd>
                    </div>
                  )}

                  <div>
                    <dt className="text-sm font-medium text-gray-500">Dimensões</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {product.height && product.width && product.length
                        ? `${product.height}×${product.width}×${product.length} cm`
                        : 'Não especificadas'}
                    </dd>
                  </div>

                  {product.weight && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Peso</dt>
                      <dd className="mt-1 text-sm text-gray-900">{product.weight} kg</dd>
                    </div>
                  )}

                  <div>
                    <dt className="text-sm font-medium text-gray-500">Data de Criação</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatDate(product.createdAt)}</dd>
                  </div>

                  {product.woocommerce && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">WooCommerce ID</dt>
                      <dd className="mt-1 text-sm text-gray-900">{product.woocommerce.woocommerce_id}</dd>
                    </div>
                  )}
                </dl>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => navigate(`/dashboard/products/pendingProducts/${product.id}/edit`)}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                >
                  Editar Produto
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
